import { createContext, useCallback, useEffect, useState, type ReactNode } from 'react';
import { get, post } from '@/lib/api-client';
import type { AuthUser, LoginResponse, SignupResponse } from '@/types/auth';

export interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  mustChangePassword: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (orgName: string, email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  acceptInvite: (token: string, password: string, firstName: string, lastName: string) => Promise<void>;
  logout: () => void;
  clearMustChangePassword: () => void;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

const STORAGE_KEYS = {
  token: 'ppe_token',
  user: 'ppe_user',
  mustChangePassword: 'ppe_must_change_password',
} as const;

function loadStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.user);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

function loadStoredToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.token);
}

function loadMustChangePassword(): boolean {
  return localStorage.getItem(STORAGE_KEYS.mustChangePassword) === 'true';
}

function persistAuth(token: string, user: AuthUser, mustChange: boolean) {
  localStorage.setItem(STORAGE_KEYS.token, token);
  localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
  localStorage.setItem(STORAGE_KEYS.mustChangePassword, String(mustChange));
}

function clearAuth() {
  localStorage.removeItem(STORAGE_KEYS.token);
  localStorage.removeItem(STORAGE_KEYS.user);
  localStorage.removeItem(STORAGE_KEYS.mustChangePassword);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(loadStoredUser);
  const [token, setToken] = useState<string | null>(loadStoredToken);
  const [mustChangePassword, setMustChangePassword] = useState(loadMustChangePassword);
  const [loading, setLoading] = useState(!!loadStoredToken());

  // Bootstrap: validate stored token against /api/auth/me
  useEffect(() => {
    const storedToken = loadStoredToken();
    if (!storedToken) {
      setLoading(false);
      return;
    }

    get<AuthUser>('/auth/me')
      .then((freshUser) => {
        setUser(freshUser);
        localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(freshUser));
      })
      .catch(() => {
        // Token invalid or expired
        clearAuth();
        setUser(null);
        setToken(null);
        setMustChangePassword(false);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data = await post<LoginResponse>('/auth/login', { email, password });
    persistAuth(data.token, data.user, data.must_change_password);
    setToken(data.token);
    setUser(data.user);
    setMustChangePassword(data.must_change_password);
  }, []);

  const signup = useCallback(async (orgName: string, email: string, password: string, firstName: string, lastName: string) => {
    const data = await post<SignupResponse>('/auth/signup', { orgName, email, password, firstName, lastName });
    persistAuth(data.token, data.user, false);
    setToken(data.token);
    setUser(data.user);
    setMustChangePassword(false);
  }, []);

  const acceptInvite = useCallback(async (inviteToken: string, password: string, firstName: string, lastName: string) => {
    const data = await post<SignupResponse>('/auth/accept-invite', { token: inviteToken, password, firstName, lastName });
    persistAuth(data.token, data.user, false);
    setToken(data.token);
    setUser(data.user);
    setMustChangePassword(false);
  }, []);

  const logout = useCallback(() => {
    // Fire-and-forget token blacklist
    post('/auth/logout').catch(() => {});
    clearAuth();
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
  }, []);

  const clearMustChangePassword = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.mustChangePassword, 'false');
    setMustChangePassword(false);
  }, []);

  const refreshUser = useCallback(async () => {
    const freshUser = await get<AuthUser>('/auth/me');
    setUser(freshUser);
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(freshUser));
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, token, mustChangePassword, loading, login, signup, acceptInvite, logout, clearMustChangePassword, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}
